import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { useContext } from 'react';
import {
  CategoryEnum,
  FloorPlan,
  FloorPlanEnum,
} from '../../../__generated__/graphql';
import { AppContext } from '../../../AppContext';
import { ROUTES } from '../../../common/constants';
import { dateFormat } from '../../../common/utils';
import useRouter from '../../../hooks/useRouter';
import { AppActionType, AppContextType } from '../../../types/appContext.type';
import { FloorPlanData } from '../../../types/dashboard.type';

//Import css
import '../styles/floorPlanCard.less';

//Import Images
import PlaceholderImage from '../../../assets/images/dashboard-placeholder-image.png';
import Disabled_Button_Icon from '../../../assets/svg/DisableThin.svg';
import Button_Icon from '../../../assets/svg/Thin.svg';

type FloorPlanCardProps = {
  plan: FloorPlan;
  setDeleteConfirmation: (value: boolean) => void;
  setSelectedPlan: (value: FloorPlan) => void;
};

export default function FloorPlanCard({
  plan,
  setDeleteConfirmation,
  setSelectedPlan,
}: FloorPlanCardProps) {
  const { dispatch } = useContext(AppContext) as AppContextType;
  const { navigate } = useRouter();

  const handleStoreFloorPlanData = (plan: FloorPlan) => {
    if (plan.assets && plan.assets.length > 0) {
      const floorPlanData = {
        id: plan?.id,
        name: plan?.name,
        propertyType: plan?.propertyType,
        floorNumber: plan?.floorNumber,
        assets: plan?.assets
          ?.filter((asset) => asset?.category === CategoryEnum.Base)
          ?.slice(0, 1)
          ?.map((asset) => ({
            key: asset?.key,
            category: asset?.category,
            width: asset?.width,
            height: asset?.height,
          })),
      };
      dispatch({
        type: AppActionType.setFloorPlanSequence,
        data: floorPlanData as FloorPlanData,
      });
      if (plan?.status === FloorPlanEnum.Draft) {
        navigate(ROUTES.UPLOAD_FLOOR_PLAN);
      }
    }
  };

  const handleRedirectOnDetails = (plan: FloorPlan) => {
    if (plan.assets && plan.assets.length > 0) {
      const floorPlanData = {
        id: plan?.id,
        name: plan?.name,
        propertyType: plan?.propertyType,
        floorNumber: plan?.floorNumber,
        resultScore: plan?.resultScore,
        companyName: plan?.companyName,
        address: plan?.address,
        assets: plan?.assets?.map((asset) => ({
          key: asset?.key,
          category: asset?.category,
        })),
      };
      dispatch({
        type: AppActionType.setFloorPlan,
        data: floorPlanData,
      });
      if (plan?.status === FloorPlanEnum.Success) {
        navigate(ROUTES.VASTU_REPORT.replace(':id', plan?.id as string));
      } else if (plan.status === FloorPlanEnum.AnnotationSuccess) {
        navigate(ROUTES.VASTU_ANNOTATION.replace(':id', plan?.id as string));
      }
    }
  };

  return (
    <div
      className={`floor-plan-card ${
        plan?.status === FloorPlanEnum.Draft ||
        plan?.status === FloorPlanEnum.AnnotationSuccess ||
        plan?.status === FloorPlanEnum.Success
          ? 'pointer'
          : 'cursor-disable'
      }`}
      onClick={() => {
        plan?.status === FloorPlanEnum.Draft
          ? handleStoreFloorPlanData(plan)
          : plan?.status === FloorPlanEnum.AnnotationSuccess ||
              plan?.status === FloorPlanEnum.Success
            ? handleRedirectOnDetails(plan)
            : null;
      }}
    >
      {(plan?.status === FloorPlanEnum.Success ||
        plan?.status === FloorPlanEnum.Failed) && (
        <>
          <div className="floor-plan-overlay"></div>
          <div
            className={`delete-icon-center`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPlan(plan);
              setDeleteConfirmation(true);
            }}
          >
            <DeleteOutlined />
          </div>
        </>
      )}
      <div className="floor-plan-img-div">
        <img
          src={
            plan?.assets?.find((asset) => asset?.category === CategoryEnum.Base)
              ?.key ??
            plan?.assets?.[0]?.key ??
            PlaceholderImage
          }
          alt="Image"
          className="floor-plan-card-image"
        />
      </div>
      <div className="floor-plan-card-description">
        <div className="floor-plan-card-description-text">
          <div className="plan-name body-sm semibold">{plan?.name}</div>
          <div className="body-meta">
            {dateFormat(plan?.createdAt ?? new Date().toISOString())}
          </div>
        </div>
        <div className="floor-plan-card-description-action">
          <div className="status-div">
            {plan?.status === FloorPlanEnum.AnnotationSuccess && (
              <Tag icon={<CheckCircleOutlined />} color="orange">
                Annotated
              </Tag>
            )}
            {plan?.status === FloorPlanEnum.Success && (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Success
              </Tag>
            )}
            {plan?.status === FloorPlanEnum.Failed && (
              <Tag icon={<WarningOutlined />} color="error">
                Failed
              </Tag>
            )}
            {plan?.status === FloorPlanEnum.Draft && (
              <Tag icon={<ExclamationCircleOutlined />} color="default">
                Draft
              </Tag>
            )}
            {plan?.status === FloorPlanEnum.AnnotationProcessing && (
              <Tag icon={<SyncOutlined spin />} color="processing">
                Processing
              </Tag>
            )}
          </div>
          {plan?.status === FloorPlanEnum.Draft ? (
            <Button type="link" onClick={() => handleStoreFloorPlanData(plan)}>
              <img src={Button_Icon} alt="Disabled" />
            </Button>
          ) : plan?.status === FloorPlanEnum.AnnotationSuccess ||
            plan?.status === FloorPlanEnum.Success ? (
            <Button
              type="link"
              onClick={() => {
                handleRedirectOnDetails(plan);
              }}
            >
              <img src={Button_Icon} alt="View" />
            </Button>
          ) : (
            <Button type="link" disabled>
              <img src={Disabled_Button_Icon} alt="Disabled" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
